// ABOUTME: Global wind helpers for readable, coherent pennant orientation
// ABOUTME: Wind is scene-level; stored pennant rotation is treated as small jitter

export const DEFAULT_WIND_CROSSWIND_DEG = 88;
export const DEFAULT_WIND_JITTER_MAX_RAD = Math.PI / 36; // 5deg — flags fly steady with the wind, faint natural variation

function finiteOr(value, fallback) {
    return Number.isFinite(value) ? value : fallback;
}

function stableUnitHash(value) {
    let h = 2166136261;
    const input = String(value ?? '');
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0) / 4294967296;
}

export function normalizeHorizontal({ x = 0, z = 0 } = {}, fallback = { x: 1, z: 0 }) {
    const safeX = finiteOr(x, 0);
    const safeZ = finiteOr(z, 0);
    const length = Math.hypot(safeX, safeZ);
    if (length < 1e-6) {
        return normalizeHorizontal(fallback, { x: 1, z: 0 });
    }
    return { x: safeX / length, z: safeZ / length };
}

export function rotateHorizontal(direction, degrees) {
    const unit = normalizeHorizontal(direction);
    const radians = degrees * Math.PI / 180;
    const c = Math.cos(radians);
    const s = Math.sin(radians);
    return normalizeHorizontal({
        x: unit.x * c + unit.z * s,
        z: -unit.x * s + unit.z * c,
    });
}

export function windDirectionFromCameraPose(pose, {
    crosswindDeg = DEFAULT_WIND_CROSSWIND_DEG,
} = {}) {
    const forward = normalizeHorizontal({
        x: (pose?.target?.x ?? 0) - (pose?.position?.x ?? 0),
        z: (pose?.target?.z ?? 0) - (pose?.position?.z ?? 0),
    }, { x: 0, z: -1 });
    return rotateHorizontal(forward, crosswindDeg);
}

export function rotationForWindDirection(windDirection) {
    const wind = normalizeHorizontal(windDirection);
    return Math.atan2(-wind.z, wind.x);
}

export function clampWindJitter(rotation = 0, {
    maxJitterRad = DEFAULT_WIND_JITTER_MAX_RAD,
    seed = null,
} = {}) {
    const limit = Math.max(0, finiteOr(maxJitterRad, DEFAULT_WIND_JITTER_MAX_RAD));
    const fallback = (stableUnitHash(seed) - 0.5) * 2 * limit;
    const value = finiteOr(rotation, fallback);
    return Math.max(-limit, Math.min(limit, value));
}

export function resolvePennantRotation(windRotation, {
    storedRotation = 0,
    seed = null,
    maxJitterRad = DEFAULT_WIND_JITTER_MAX_RAD,
} = {}) {
    return finiteOr(windRotation, 0) + clampWindJitter(storedRotation, { maxJitterRad, seed });
}

export function createGlobalWindFromCameraPose(pose, options = {}) {
    const direction = windDirectionFromCameraPose(pose, options);
    return {
        direction,
        rotation: rotationForWindDirection(direction),
        crosswindDeg: options.crosswindDeg ?? DEFAULT_WIND_CROSSWIND_DEG,
        jitterMaxRad: options.maxJitterRad ?? DEFAULT_WIND_JITTER_MAX_RAD,
    };
}
